import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-[9px] py-[3px] text-[11px] font-bold w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'bg-primary-soft text-primary border-primary-line',
        destructive: 'bg-destructive-soft text-destructive border-destructive/30',
        outline: 'bg-transparent text-foreground-2 border-border-strong',
        primary: 'bg-primary-soft text-primary border-primary-line',
        secondary: 'bg-muted text-muted-foreground border-border',
        third: 'bg-muted text-foreground-2 border-border',
        ghost: 'bg-transparent border-transparent text-muted-foreground hover:bg-muted',
        link: 'bg-transparent border-transparent text-primary underline-offset-4 hover:underline',
        none: 'bg-transparent border-transparent text-foreground-2',
        success: 'bg-success-soft text-success border-success/30',
        warning: 'bg-warning-soft text-warning border-warning/30',
        info: 'bg-primary-soft text-primary border-primary-line',
        error: 'bg-destructive-soft text-destructive border-destructive/30',
        green: 'bg-success-soft text-success border-success/30',
        blue: 'bg-primary-soft text-primary border-primary-line',
        purple: 'bg-[#6E56CF1F] text-[#6E56CF] border-[#6E56CF4D]',
        orange: 'bg-warning-soft text-warning border-warning/30',
        yellow: 'bg-warning-soft text-warning border-warning/30',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

const getBadgeVariant = (type: string | boolean) => {
  switch (type) {
    case 'received':
      return 'success';
    case 'failed':
      return 'destructive';
    case true:
      return 'success';
    case false:
      return 'destructive';
    case 'success':
      return 'success';
    case 'error':
      return 'destructive';
    default:
      return 'default';
  }
};

const getBadgeLabel = (type: string | boolean) => {
  switch (type) {
    case 'received':
      return 'Recebido';
    case 'failed':
      return 'Falha';
    case true:
      return 'Sim';
    case false:
      return 'Não';
    case 'success':
      return 'Sucesso';
    case 'error':
      return 'Falha';
    default:
      return 'Desconhecido';
  }
};

export { Badge, badgeVariants, getBadgeLabel, getBadgeVariant };
